import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { cn } from '../../lib/utils';

interface MarkdownContentProps {
  content: string;
  className?: string;
}

export function MarkdownContent({ content, className }: MarkdownContentProps) {
  return (
    <div className={cn('text-sm leading-relaxed', className)}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          h2: ({ children }) => (
            <h2 className="text-base font-semibold text-text-primary mt-3 mb-1.5">{children}</h2>
          ),
          h3: ({ children }) => (
            <h3 className="text-sm font-semibold text-text-primary mt-2 mb-1">{children}</h3>
          ),
          p: ({ children }) => <p className="mb-1.5 last:mb-0">{children}</p>,
          strong: ({ children }) => (
            <strong className="font-semibold text-text-primary">{children}</strong>
          ),
          em: ({ children }) => <em className="text-text-secondary italic">{children}</em>,
          table: ({ children }) => (
            <div className="my-2 overflow-x-auto rounded-lg border border-surface-600">
              <table className="w-full text-xs">{children}</table>
            </div>
          ),
          thead: ({ children }) => (
            <thead className="bg-surface-800/80 border-b border-surface-600">{children}</thead>
          ),
          th: ({ children }) => (
            <th className="px-2.5 py-1.5 text-left font-semibold text-text-secondary uppercase tracking-wider text-[10px]">
              {children}
            </th>
          ),
          td: ({ children }) => (
            <td className="px-2.5 py-1.5 text-text-primary border-t border-surface-700">
              {children}
            </td>
          ),
          code: ({ children, className: codeClassName }) => {
            const isInline = !codeClassName;
            if (isInline) {
              return (
                <code className="px-1 py-0.5 text-[11px] font-mono bg-surface-700 rounded text-primary-400">
                  {children}
                </code>
              );
            }
            return (
              <pre className="my-2 px-3 py-2 text-[11px] font-mono bg-surface-900 rounded-lg border border-surface-600 overflow-x-auto">
                <code>{children}</code>
              </pre>
            );
          },
          ul: ({ children }) => (
            <ul className="my-1.5 ml-4 space-y-0.5 list-disc list-outside">{children}</ul>
          ),
          ol: ({ children }) => (
            <ol className="my-1.5 ml-4 space-y-0.5 list-decimal list-outside">{children}</ol>
          ),
          li: ({ children }) => <li className="text-text-secondary">{children}</li>,
          a: ({ children, href }) => (
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary-400 hover:text-primary-300 underline"
            >
              {children}
            </a>
          ),
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
}
